import { useTranslation } from 'react-i18next';
import { ASITokenClaimsDisclaimer } from './ASITokenClaimsDisclaimer';
import { Body2, Normal2, Tiny1, Tiny2, Title4 } from 'components/Typography';
import Image from 'next/image';
import { getStaticAssetUrl } from 'utils/url';
import { ConnectButton } from 'components/ConnectButton';
import { useClientRenderDelay } from 'utils/hooks/useClientRenderDelay';
import { useConnection, useSwitchChain, useWalletClient } from 'wagmi';
import { Button } from 'baseui/button';
import { cx } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';
import styles from '../../../migration/components/MigrationPanel/migrationpanel.module.css';
import { useSDAOLaunchpadClaim } from 'structure/claim/hooks/useSDAOLaunchpadClaim';
import { userClaimableListTestnet } from 'utils/constants/claim.const';
import { displayBalance } from 'utils/balance';
import { useUser } from 'structure/user/UserContext';
import { useToast } from '@dapp/multi-lib/ui/toast';
import { useState } from 'react';

const InfoRow = ({
  label,
  value,
  highlight = false,
  sfiLabel = false,
}: {
  label: string;
  value: string | number;
  highlight?: boolean;
  sfiLabel?: boolean;
}) => (
  <div className='mb-4 flex justify-between gap-5'>
    <Title4 className='font-normal text-white-500 lg:text-[0.875rem]'>
      {label}
    </Title4>
    <Title4
      className={`font-normal lg:text-[0.875rem] ${highlight ? 'text-lightYellow' : 'text-white-200'}`}
    >
      {value} {sfiLabel && <Tiny2 className='text-white-500'>SFI</Tiny2>}
    </Title4>
  </div>
);

export const ClaimPagePanelTestnet = () => {
  const { t } = useTranslation('claim');
  const { switchChain } = useSwitchChain();
  const toastV2 = useToast();
  const csr = useClientRenderDelay();
  const { isConnected, chainId } = useConnection();
  const { account } = useUser();
  const [isDisableButton, setIsDisableButton] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { data: walletClient } = useWalletClient();
  const { data: claimData, refetch } = useSDAOLaunchpadClaim(
    userClaimableListTestnet?.projectClaimContractAddress,
    userClaimableListTestnet.poolId
  );
  const checkSelectedPoolNetwork =
    userClaimableListTestnet?.poolChainId !== chainId;
  const isClaimableAmountZero = Number(claimData?.claimableAmount) === 0;
  const isClaimAvailable = Boolean(claimData?.userInfo?.claimed);
  const buttonDisabled =
    !checkSelectedPoolNetwork &&
    (isClaimableAmountZero || isDisableButton || !claimData);
  const handleNetworkChange = () => {
    return switchChain?.({ chainId: userClaimableListTestnet?.poolChainId });
  };
  const addTokenToMetamask = async () => {
    try {
      await walletClient?.watchAsset({
        type: 'ERC20',
        options: {
          address: userClaimableListTestnet?.sfiToken,
          symbol: userClaimableListTestnet?.symbol,
          decimals: userClaimableListTestnet?.decimals,
        },
      });
    } catch (error) {
      console.info(error);
    }
  };

  const handleClaim = async () => {
    setIsLoading(true);
    try {
      if (!claimData?.launchPadClaimcontract || !account) {
        return null;
      }
      if (isClaimableAmountZero) {
        toastV2({
          title: t('common:ERROR_MESSAGES.ERROR'),
          description: t(
            'common:ERROR_MESSAGES.FAILED_DUE_TO_INSUFFICIENT_TOKEN'
          ),
          type: 'error',
        });
        return;
      }
      setIsDisableButton(true);
      const txn = await claimData!.claim();
      await txn.wait();
      await refetch();
      setIsLoading(false);
      setIsDisableButton(false);
      toastV2({
        title: t('common:ERROR_MESSAGES.SUCCESS'),
        description: t('common:ERROR_MESSAGES.CLAIM_SUCCESSFUL'),
        type: 'success',
      });
    } catch (error) {
      console.error(error);
      setIsDisableButton(false);
      toastV2({
        title: t('common:ERROR_MESSAGES.ERROR'),
        description: t('common:ERROR_MESSAGES.CLAIM_ERROR'),
        type: 'error',
      });
    }
  };

  return (
    <div className='flex flex-col items-start gap-5 lg:flex-row lg:gap-24'>
      <div className='flex h-full w-full flex-col gap-5 border border-black-300 bg-black p-4 lg:w-1/2 lg:p-8'>
        <Normal2 className='font-bold text-white-400'>
          {t('CLAIM_YOUR_TOKENS')}
        </Normal2>
        <div>
          <InfoRow
            label={t('ALLOCATION')}
            value={
              displayBalance(
                claimData?.userInfo?.allocated || '0',
                userClaimableListTestnet?.decimals,
                true
              ) ?? 'N/A'
            }
            sfiLabel={true}
          />
          <InfoRow
            label={t('START_DATE')}
            value={claimData?.poolInfo?.emissionPeriod?.start ?? '---'}
          />
          <InfoRow
            label={t('END_DATE')}
            value={claimData?.poolInfo?.emissionPeriod?.end ?? '---'}
          />
          <InfoRow
            label={t('CLAIMED')}
            value={
              displayBalance(
                claimData?.userInfo?.claimed || '0',
                userClaimableListTestnet?.decimals,
                true
              ) ?? 'N/A'
            }
            sfiLabel={true}
          />
          <InfoRow
            label={t('CLAIMABLE')}
            value={
              displayBalance(
                claimData?.claimableAmount || '0',
                userClaimableListTestnet?.decimals,
                true
              ) ?? 'N/A'
            }
            sfiLabel={true}
            highlight
          />
          <div className='flex justify-end'>
            <button
              className='flex cursor-pointer items-center space-x-2 rounded-full text-white'
              onClick={addTokenToMetamask}
            >
              <Body2>{t('ADD_TO_WALLET')}</Body2>
              <Image
                alt='meta-mast'
                width={24}
                height={24}
                src={getStaticAssetUrl(
                  '/sfi/static/image/wallets/metamask.svg'
                )}
              />
            </button>
          </div>
        </div>
        {!isConnected && csr && (
          <Tiny1 className='text-negative-800'>
            {t('CONNECT_YOUR_WALLET_TO_SEE')}
          </Tiny1>
        )}
        {isConnected && csr && isClaimableAmountZero && !isClaimAvailable && (
          <Tiny1 className='text-negative-800'>
            {t('THE_WALLET_HAS_REWARDS_ALLOCATED')}{' '}
            <Tiny1 className='text-lightYellow'>
              {t('PLEASE_CONNECT_WALLET_USED_PARTICIPATE_SFI_TESTNET_CAMPAIGN')}
            </Tiny1>
          </Tiny1>
        )}
        {!isConnected && csr && (
          <div className='flex w-full flex-row justify-center'>
            <ConnectButton className='w-full px-10 py-1 text-sm hover:bg-white hover:text-black-1000' />
          </div>
        )}
        {isConnected && csr && (
          <Button
            disabled={buttonDisabled}
            isLoading={isLoading}
            className={cx(
              twMerge(
                styles.button,
                'w-full !p-2 !font-normal',
                buttonDisabled && '!bg-box-borderLight !text-mono-100'
              )
            )}
            onClick={
              checkSelectedPoolNetwork ? handleNetworkChange : handleClaim
            }
          >
            {checkSelectedPoolNetwork
              ? t('migrate:SWITCH_NETWORK')
              : t('CLAIM')}
          </Button>
        )}
      </div>
      <div className='flex w-full flex-col gap-5 lg:w-1/2'>
        <ASITokenClaimsDisclaimer />
      </div>
    </div>
  );
};
